/**
 * Ask-AI — typed Eden Treaty wrappers for `/api/companies/:companyId/ask-ai`.
 *
 * The backend wraps every payload in `success(...)` so the wire body is
 * `{ data: ... }` on 200 and `{ error: { code, message } }` on the failure
 * branches the handlers set via `set.status`. We surface the three meaningful
 * failure statuses as dedicated error classes so the page can render distinct
 * states (out of credits / session gone / engine unavailable) rather than a
 * generic toast, and pass the `status: "warming"` payload straight through so
 * the caller can retry with backoff. Mirrors `lib/api/search.ts`.
 */

import type { CompanyApi } from "../../hooks/useCompanyApi.ts"
import type { IAskExample, IAskPayload, IAskScope, IAskSessionDetail, IAskSessionSummary } from "../types/ask.ts"
import { extractErrorMessage } from "./errors.ts"

/** Thrown on 402 — the org is out of platform credits / over budget, so no new
 * discretionary work is allowed. The UI offers a distinct "out of credits" state. */
export class AskOutOfCreditsError extends Error {
  constructor(message: string) {
    super(message)
    this.name = "AskOutOfCreditsError"
  }
}

/** Thrown on 404 — the referenced chat session doesn't exist (or isn't the
 * caller's). The UI drops back to a fresh chat. */
export class AskSessionNotFoundError extends Error {
  constructor(message: string) {
    super(message)
    this.name = "AskSessionNotFoundError"
  }
}

/** Thrown on 503 — the embedding service is down or unconfigured. Distinct from
 * a transient error so the UI can offer a "try again" affordance with the right
 * copy. */
export class AskUnavailableError extends Error {
  constructor(message: string) {
    super(message)
    this.name = "AskUnavailableError"
  }
}

export interface IAskRequest {
  question: string
  /** Continue an existing chat; omit to start a new session. */
  sessionId?: string
  /** Restrict retrieval to a subset (this month / a series). Omit for all meetings. */
  scope?: IAskScope
}

/**
 * Map an Eden Treaty error envelope onto the right typed error. Eden types the
 * error status as the route's validation default (the handlers declare only a
 * 200 schema); at runtime it carries the real HTTP status the handler sets, so
 * we widen to `number` before comparing.
 */
function throwForStatus(error: { status: number; value?: unknown }): never {
  const httpStatus: number = error.status
  const message = extractErrorMessage(error)
  if (httpStatus === 402) throw new AskOutOfCreditsError(message)
  if (httpStatus === 404) throw new AskSessionNotFoundError(message)
  if (httpStatus === 503) throw new AskUnavailableError(message)
  throw new Error(message)
}

/**
 * Ask a grounded question about the active company's meetings. Resolves with the
 * payload (which may carry `status: "warming"` when the engine is still spinning
 * up — the caller retries with backoff). Rejects with `AskOutOfCreditsError`
 * (402), `AskSessionNotFoundError` (404), or `AskUnavailableError` (503).
 */
export async function askQuestion(company: CompanyApi, req: IAskRequest): Promise<IAskPayload> {
  // Build the body as our own request type so the additive `scope` field (added
  // by Phase 1 backend) type-checks whether or not the in-tree Eden types carry
  // it yet — a variable with an extra optional prop stays assignable to the
  // route's body type. `scope` is only attached when a non-default scope is set,
  // so the "all meetings" path sends the exact same body as before.
  const body: IAskRequest = { question: req.question, sessionId: req.sessionId }
  if (req.scope) body.scope = req.scope
  const result = await company["ask-ai"].ask.post(body)

  if (result.error) throwForStatus(result.error)

  const data = result.data
  if (data && "data" in data) return data.data

  // 200 with an error envelope — shouldn't happen (errors carry a non-2xx
  // status), but keep the type union honest rather than returning junk.
  throw new Error(extractErrorMessage(data))
}

/**
 * Fetch suggested example questions for the empty state (up to 4). Best-effort:
 * the caller falls back to static starters when this rejects.
 *
 * NOTE: `GET /ask-ai/examples` is added by the concurrent Phase 1 backend work.
 * Until that lands in the tree the Eden client won't know the route and this
 * single call site is the only place the frontend tsc gate can fail — it resolves
 * itself once the backend endpoint is present (no frontend change needed).
 */
export async function fetchAskExamples(company: CompanyApi): Promise<IAskExample[]> {
  const result = await company["ask-ai"].examples.get()

  if (result.error) throwForStatus(result.error)

  const body = result.data
  if (body && "data" in body) return body.data
  throw new Error(extractErrorMessage(body))
}

/** List the caller's chat sessions (newest activity first). */
export async function listAskSessions(company: CompanyApi): Promise<IAskSessionSummary[]> {
  const result = await company["ask-ai"].sessions.get()

  if (result.error) throwForStatus(result.error)

  const body = result.data
  if (body && "data" in body) return body.data
  throw new Error(extractErrorMessage(body))
}

/** Fetch one chat session with its full message history. Rejects with
 * `AskSessionNotFoundError` (404) when the session isn't the caller's. */
export async function getAskSession(company: CompanyApi, sessionId: string): Promise<IAskSessionDetail> {
  const result = await company["ask-ai"].sessions({ sessionId }).get()

  if (result.error) throwForStatus(result.error)

  const body = result.data
  if (body && "data" in body) return body.data
  throw new Error(extractErrorMessage(body))
}

/** Delete a chat session (its messages cascade). Rejects with
 * `AskSessionNotFoundError` (404) when the session isn't the caller's. */
export async function deleteAskSession(company: CompanyApi, sessionId: string): Promise<void> {
  const result = await company["ask-ai"].sessions({ sessionId }).delete()

  if (result.error) throwForStatus(result.error)
}
